import { FileUploadZone } from "@/components/common/FileUploadZone/FileUploadZone"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { useObjectUrl } from "@/hooks/useObjectUrl"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useMutation } from "@tanstack/react-query"
import { FileText, Loader2, Paperclip, X } from "lucide-react"
import { useState, type ClipboardEvent } from "react"
import type { FileWithPath } from "react-dropzone"
import { NOTE_MAX_BODY_CHARS } from "../../../api/notesApi"
import {
  clipboardFiles,
  NOTE_FILE_TYPES,
  screenNoteFiles,
  type NoteFileProblem,
} from "./noteFiles"

const PROBLEM_KEY: Record<NoteFileProblem, TranslationKeys> = {
  unsupported: "MC_NOTE_ERR_UNSUPPORTED_FILE",
  tooLarge: "MC_NOTE_ERR_FILE_TOO_LARGE",
  limit: "MC_NOTE_ERR_ATTACHMENT_LIMIT",
}

// FileUploadZone is shaped around uploading each dropped file at once. Here the
// files are only staged until the note is saved: `onFilesAccepted` keeps them, and
// these satisfy the props the zone requires but never calls.
const noop = () => {}
function useNoUpload() {
  return useMutation<void, Error, FileWithPath>({ mutationFn: async () => {} })
}

export interface NoteDraft {
  body: string
  files: File[]
}

function StagedFile({
  file,
  disabled,
  onRemove,
}: {
  file: File
  disabled: boolean
  onRemove: () => void
}) {
  const { translate } = useTranslation()
  const isImage = file.type.startsWith("image/")
  const preview = useObjectUrl(isImage ? file : null)
  return (
    <li className="flex w-full items-center gap-2 rounded-md border border-grey4 bg-grey3 py-1 pr-2 pl-1 text-[12px] text-white">
      {preview ? (
        <img src={preview} alt="" className="h-8 w-8 rounded object-cover" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center text-grey5">
          <FileText size={16} />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{file.name}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={onRemove}
        aria-label={translate("MC_NOTE_FILE_REMOVE", { name: file.name })}
        className="shrink-0 text-grey5 hover:text-white disabled:opacity-50"
      >
        <X size={13} />
      </button>
    </li>
  )
}

interface NoteDraftFieldsProps {
  draft: NoteDraft
  onChange: (draft: NoteDraft) => void
  label: string
  placeholder?: string
  /** Files the note already stores: they count toward the five-file limit. */
  existingAttachmentCount?: number
  disabled?: boolean
  autoFocus?: boolean
}

/**
 * The text area and the attachment zone, controlled by the caller. Shared by the
 * composer below and the close dialog, which submit the draft in their own way.
 * Files arrive by paste (Ctrl+V on a screenshot), drag and drop, or the file picker.
 */
export function NoteDraftFields({
  draft,
  onChange,
  label,
  placeholder,
  existingAttachmentCount = 0,
  disabled = false,
  autoFocus = false,
}: NoteDraftFieldsProps) {
  const { translate } = useTranslation()

  function addFiles(incoming: File[]) {
    const { accepted, problems } = screenNoteFiles(
      incoming,
      existingAttachmentCount + draft.files.length,
    )
    for (const problem of problems) toast.error(translate(PROBLEM_KEY[problem]))
    if (accepted.length > 0)
      onChange({ ...draft, files: [...draft.files, ...accepted] })
  }

  function handlePaste(event: ClipboardEvent<HTMLElement>) {
    const files = clipboardFiles(event.clipboardData)
    if (files.length === 0) return
    // Only a file paste is taken over: text still pastes into the field as usual.
    event.preventDefault()
    addFiles(files)
  }

  return (
    <div className="flex flex-col gap-2" onPaste={handlePaste}>
      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-grey5">{label}</span>
        <Textarea
          value={draft.body}
          onChange={(e) => onChange({ ...draft, body: e.target.value })}
          placeholder={placeholder}
          maxLength={NOTE_MAX_BODY_CHARS}
          disabled={disabled}
          autoFocus={autoFocus}
          rows={3}
          className="resize-y text-[13px]"
        />
      </label>
      <FileUploadZone
        mimeTypes={NOTE_FILE_TYPES}
        setIsPending={noop}
        setError={noop}
        setFiles={noop}
        useUploadMutation={useNoUpload}
        onFilesAccepted={(files) => {
          addFiles(files)
          return true
        }}
        isDisabled={disabled}
        // A drop or a pick of the wrong type is turned away by the zone itself;
        // this gives it the copy a paste of that file gets.
        getFileRejectionMessage={() => translate(PROBLEM_KEY.unsupported)}
        idleContent={
          <div className="flex items-center gap-2 rounded-md border border-dashed border-grey4 px-3 py-2.5 text-[12px] text-grey5 hover:border-grey5">
            <Paperclip size={14} className="shrink-0" />
            <span>{translate("MC_NOTE_DROP_HINT")}</span>
          </div>
        }
      />
      {draft.files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {draft.files.map((file, index) => (
            <StagedFile
              key={`${file.name}-${index}`}
              file={file}
              disabled={disabled}
              onRemove={() =>
                onChange({
                  ...draft,
                  files: draft.files.filter((_, i) => i !== index),
                })
              }
            />
          ))}
        </ul>
      )}
    </div>
  )
}

export interface NoteComposerValue extends NoteDraft {
  warnOnInquiry: boolean
}

interface NoteComposerProps {
  submitLabel: string
  /** Set when editing: the note's current text and warning flag. */
  initialBody?: string
  initialWarn?: boolean
  existingAttachmentCount?: number
  /** Changes the owner holds that the fields do not show (staged attachment
   *  removals): they make saving worthwhile on their own. */
  hasPendingChanges?: boolean
  /** Supplier notes can ask for a confirmation on every new send. */
  showWarnToggle?: boolean
  onSubmit: (value: NoteComposerValue) => Promise<unknown>
  onCancel?: () => void
  autoFocus?: boolean
}

/** A text area, an attachment zone and a save button: adds a note or edits one. */
export function NoteComposer({
  submitLabel,
  initialBody = "",
  initialWarn = false,
  existingAttachmentCount = 0,
  hasPendingChanges = false,
  showWarnToggle = false,
  onSubmit,
  onCancel,
  autoFocus = false,
}: NoteComposerProps) {
  const { translate } = useTranslation()
  const [draft, setDraft] = useState<NoteDraft>({
    body: initialBody,
    files: [],
  })
  const [warnOnInquiry, setWarnOnInquiry] = useState(initialWarn)
  const [submitting, setSubmitting] = useState(false)

  const body = draft.body.trim()
  const dirty =
    body !== initialBody.trim() ||
    draft.files.length > 0 ||
    warnOnInquiry !== initialWarn ||
    hasPendingChanges

  async function submit() {
    setSubmitting(true)
    try {
      await onSubmit({ body, files: draft.files, warnOnInquiry })
      setDraft({ body: initialBody, files: [] })
      setWarnOnInquiry(initialWarn)
    } catch {
      // The mutation already toasted; the draft stays so nothing typed is lost.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <NoteDraftFields
        draft={draft}
        onChange={setDraft}
        label={translate("MC_NOTE_BODY_LABEL")}
        placeholder={translate("MC_NOTE_PLACEHOLDER")}
        existingAttachmentCount={existingAttachmentCount}
        disabled={submitting}
        autoFocus={autoFocus}
      />
      {showWarnToggle && (
        <label className="flex items-center gap-2 text-[12.5px] text-white">
          <Checkbox
            checked={warnOnInquiry}
            onCheckedChange={(checked) => setWarnOnInquiry(checked === true)}
            disabled={submitting}
          />
          {translate("MC_NOTE_WARN_ON_INQUIRY")}
        </label>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={submitting}
            onClick={onCancel}
          >
            {translate("MC_DECL_BTN_CANCEL")}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          className="bg-yellow1 text-black hover:bg-yellow2"
          disabled={!dirty || body.length === 0 || submitting}
          onClick={() => void submit()}
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}
